import loadable from '@loadable/component';
import { OverlaySpinner } from 'components/atoms/Spinner';
import { LanguageContextProvider } from 'components/contexts/LanguageContext';
import { RoutingContextProvider } from 'components/contexts/RoutingContext';
import NotFoundPage from 'components/pages/NotFoundPage';
import { useSiteCurrentQuery } from 'generated/graphql';
import React from 'react';
import { Helmet, HelmetTags } from 'react-helmet';
import { firebaseAnalytics } from 'services/firebase/analytics';
import { AppContextProvider } from '../components/contexts/AppContext';
import { applicationType } from './types';
import { usePWAInstall } from 'react-use-pwa-install';
import { useRegisterDevice } from 'hooks/useRegisterDevice';
import { useAuth } from 'hooks/auth/useAuth';
import { useForegroundPush } from 'hooks/useForegroundPush';
import { useBlobManifest } from 'hooks/useBlobManifest';

// import CourseAPP from './CourseApp';
// import CompanyApp from './CompanyApp';
// import CommonApp from './CommonApp';

const AsyncApp = loadable(
  (props: { appType: applicationType; landing?: string }) =>
    import(`./${props.appType}`),
  {
    fallback: <OverlaySpinner />,
  },
);

// AsyncApp.preload({appType:applicationType.course})

export const AppManagerProvider: React.FC = (props) => {
  const { data, loading } = useSiteCurrentQuery({
    errorPolicy: 'all',
  });
  const { user } = useAuth();
  const site = data?.siteCurrent;

  const appType = React.useMemo(() => {
    if (loading) return;
    if (!site) return null;
    if (site?.company) return applicationType.company;
    if (site?.course) return applicationType.course;
    if (site?.mosaico) return applicationType.mosaico;
    if (site) return applicationType.common;
  }, [loading, site]);

  const course = site?.course;
  const mosaico = site?.mosaico;
  const company = site?.company || course?.company;
  // const theme = company?.theme || course?.company?.theme;
  const landing = process.env['REACT_APP_LANDING_PAGE'] || 'Palco';

  const pwaInstallEnabled = React.useMemo(() => {
    return (
      course?.company?.enablePwaInstall || company?.enablePwaInstall || false
    );
  }, [course, company]);
  const pushNotificationInstallEnabled = React.useMemo(() => {
    return (
      course?.company?.enablePushNotification ||
      company?.enablePushNotification ||
      false
    );
  }, [course, company]);
  // const defaultThemeId = company?.defaultTheme?.id;

  const onHelmetUpdate = React.useCallback(
    (newState: any, addedTags: HelmetTags, removedTags: HelmetTags) => {
      firebaseAnalytics.logEvent('page_view', {});
    },
    [],
  );

  /* -------------------- manifest ------------------------------------ */
  const htmlTags = site?.faviconData?.htmlTags;

  const manifestTag = htmlTags?.find(
    (t) =>
      t &&
      t.tag === 'link' &&
      t.attributes?.some((a) => a && a.key === 'rel' && a.value === 'manifest'),
  );

  const manifestHref =
    manifestTag?.attributes?.find((a) => a && a.key === 'href')?.value ??
    undefined;

  useBlobManifest(manifestHref); // 👈 crea blob same-origin

  const tagsWithoutManifest = htmlTags?.filter((t) => t !== manifestTag);

  // INSTALL PWA
  const [showInstall, setShowInstall] = React.useState(false);

  const installPWA = usePWAInstall();
  useRegisterDevice(Boolean(user) && pushNotificationInstallEnabled);
  useForegroundPush();

  const capture = React.useCallback(() => {
    if (!showInstall && installPWA && Boolean(user)) {
      installPWA();
      setShowInstall(true);
    }
  }, [installPWA, showInstall, user]);

  if (loading || appType === undefined) {
    return <OverlaySpinner />;
  }

  if (appType === null) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Helmet onChangeClientState={onHelmetUpdate}>
        <title>{company?.name}</title>

        {tagsWithoutManifest?.map((t, idx) => {
          if (!t) return null;
          const Tag = t.tag as keyof JSX.IntrinsicElements;
          const props = (t.attributes ?? []).reduce<Record<string, string>>(
            (acc, attr) => {
              if (attr && attr.key && attr.value) {
                return { ...acc, [attr.key]: attr.value };
              }
              return acc;
            },
            {},
          );
          // If the tag requires an 'object' prop, provide a dummy or appropriate value
          if (Tag === 'object') {
            return <Tag key={idx} {...props} object={{}} />;
          }
          return <Tag key={idx} {...props} />;
        })}
      </Helmet>

      <LanguageContextProvider>
        {/* <React.Suspense fallback={<OverlaySpinner />}> */}
        <RoutingContextProvider appType={appType}>
          <AppContextProvider
            appType={appType}
            company={company}
            course={course}
            mosaico={mosaico}
          >
            {/* <CourseAPP></CourseAPP> */}
            <div onClickCapture={pwaInstallEnabled ? capture : undefined}>
              <AsyncApp appType={appType} landing={landing} />
            </div>
          </AppContextProvider>
        </RoutingContextProvider>
        {/* </React.Suspense> */}
      </LanguageContextProvider>
    </>
  );
};

export default AppManagerProvider;
